import React, { useMemo } from "react";
import Button from "../../../../components/ACS-Dashboard/ACS-Blocks/Button";

const STATUS_BADGE_CLASS = {
  MATCHED:         "bg-emerald-100 dark:bg-emerald-900/40 text-black dark:text-emerald-200",
  AMBIGUOUS:       "bg-amber-100   dark:bg-amber-900/40   text-black dark:text-amber-200",
  ZERO_MATCH:      "bg-red-100     dark:bg-red-900/40     text-black dark:text-red-200",
  NO_MATCH_FIELDS: "bg-red-100     dark:bg-red-900/40     text-black dark:text-red-200",
  NO_CHANGES:      "bg-gray-100    dark:bg-gray-700/40    text-black dark:text-gray-200",
};

const STATUS_LABEL = {
  MATCHED: "Matched",
  AMBIGUOUS: "Ambiguous",
  ZERO_MATCH: "No Match",
  NO_MATCH_FIELDS: "No Match Fields",
  NO_CHANGES: "No Changes",
};

const SELECTABLE_STATUSES = new Set(["MATCHED", "AMBIGUOUS"]);

const DryRunReviewStep = ({ dryRunResult, rowDecisions, setRowDecisions, loading, onBack, onApply }) => {
  const selectableRows = useMemo(
    () => dryRunResult.rows.filter((r) => SELECTABLE_STATUSES.has(r.status)),
    [dryRunResult]
  );

  const selectedCount = selectableRows.filter((r) => rowDecisions[r.rowIndex]?.include).length;
  const allSelected = selectableRows.length > 0 && selectedCount === selectableRows.length;

  const toggleAll = (checked) => {
    setRowDecisions((prev) => {
      const next = { ...prev };
      selectableRows.forEach((r) => {
        next[r.rowIndex] = { ...next[r.rowIndex], include: checked };
      });
      return next;
    });
  };

  const toggleRow = (rowIndex, checked) => {
    setRowDecisions((prev) => ({ ...prev, [rowIndex]: { ...prev[rowIndex], include: checked } }));
  };

  const setResolution = (rowIndex, recordId) => {
    setRowDecisions((prev) => ({
      ...prev,
      [rowIndex]: { ...prev[rowIndex], resolvedRecordId: recordId, include: !!recordId },
    }));
  };

  const readyToApply = selectableRows.some((r) => {
    const decision = rowDecisions[r.rowIndex];
    if (!decision?.include) return false;
    if (r.status === "AMBIGUOUS" && !decision.resolvedRecordId) return false;
    return true;
  });

  return (
    <div className="flex flex-col gap-fluid-3">
      <div className="grid grid-cols-4 gap-fluid-2">
        <SummaryCard label="Matched" value={dryRunResult.matchedCount} tone="emerald" />
        <SummaryCard label="Ambiguous" value={dryRunResult.ambiguousCount} tone="amber" />
        <SummaryCard label="No Match" value={dryRunResult.zeroMatchCount} tone="red" />
        <SummaryCard label="No Changes" value={dryRunResult.noChangeCount} tone="gray" />
      </div>

      <div className="overflow-x-auto border border-gray-200 dark:border-acs-dark-border rounded-lg max-h-[50vh] overflow-y-auto">
        <table className="min-w-full text-fluid-sm">
          <thead className="bg-gray-50 dark:bg-acs-dark-bg text-gray-500 dark:text-acs-dark-subtext sticky top-0">
            <tr>
              <th className="px-3 py-2">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => toggleAll(e.target.checked)}
                  className="w-4 h-4 accent-acs-primary-navy dark:accent-acs-accent"
                />
              </th>
              <th className="text-left px-3 py-2 font-semibold">Row</th>
              <th className="text-left px-3 py-2 font-semibold">Status</th>
              <th className="text-left px-3 py-2 font-semibold">Record</th>
              <th className="text-left px-3 py-2 font-semibold">Changes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-acs-dark-border">
            {dryRunResult.rows.map((row) => {
              const decision = rowDecisions[row.rowIndex] || { include: false, resolvedRecordId: null };
              const selectable = SELECTABLE_STATUSES.has(row.status);
              return (
                <tr key={row.rowIndex} className="bg-white dark:bg-acs-dark-surface align-top">
                  <td className="px-3 py-2">
                    {selectable && (
                      <input
                        type="checkbox"
                        checked={!!decision.include}
                        onChange={(e) => toggleRow(row.rowIndex, e.target.checked)}
                        className="w-4 h-4 accent-acs-primary-navy dark:accent-acs-accent"
                      />
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-500 dark:text-acs-dark-subtext">{row.rowIndex + 1}</td>
                  <td className="px-3 py-2">
                    <span className={`text-fluid-xs px-fluid-1.5 py-fluid-1 rounded font-medium ${STATUS_BADGE_CLASS[row.status]}`}>
                      {STATUS_LABEL[row.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-gray-800 dark:text-acs-dark-text">
                    {row.status === "AMBIGUOUS" ? (
                      <select
                        value={decision.resolvedRecordId || ""}
                        onChange={(e) => setResolution(row.rowIndex, e.target.value || null)}
                        className="border border-gray-300 dark:border-acs-dark-border rounded-md px-2 py-1
                                   bg-white dark:bg-acs-dark-bg text-gray-800 dark:text-acs-dark-text w-full"
                      >
                        <option value="">Pick a record...</option>
                        {row.ambiguousCandidates.map((c) => (
                          <option key={c.recordId} value={c.recordId}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      row.matchedRecordLabel || <span className="text-gray-400 dark:text-acs-dark-subtext">{row.message}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {row.changes.length === 0 ? (
                      <span className="text-gray-400 dark:text-acs-dark-subtext">{row.message || "—"}</span>
                    ) : (
                      <ul className="flex flex-col gap-0.5">
                        {row.changes.map((c) => (
                          <li key={c.fieldKey} className="text-fluid-xs">
                            <span className="font-medium text-gray-700 dark:text-acs-dark-text">{c.label}:</span>{" "}
                            <span className="text-gray-400 dark:text-acs-dark-subtext line-through">{c.oldValue || "(empty)"}</span>{" "}
                            &rarr; <span className="text-emerald-700 dark:text-emerald-300">{c.newValue}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between">
        <Button text="Back" variant="outline" onClick={onBack} disabled={loading} />
        <Button
          text={loading ? "Applying..." : `Apply ${selectedCount} Change${selectedCount === 1 ? "" : "s"}`}
          onClick={onApply}
          disabled={loading || !readyToApply}
          className="!w-auto whitespace-nowrap !px-fluid-4"
        />
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value, tone }) => {
  const toneClass = {
    emerald: "text-emerald-700 dark:text-emerald-300",
    amber: "text-amber-700 dark:text-amber-300",
    red: "text-red-700 dark:text-red-300",
    gray: "text-gray-700 dark:text-acs-dark-subtext",
  }[tone];
  return (
    <div className="border border-gray-200 dark:border-acs-dark-border rounded-lg p-fluid-2 bg-white dark:bg-acs-dark-surface">
      <div className={`text-fluid-xl font-bold ${toneClass}`}>{value}</div>
      <div className="text-fluid-xs text-gray-500 dark:text-acs-dark-subtext">{label}</div>
    </div>
  );
};

export default DryRunReviewStep;
